import api from './client';

export interface ArchitectureRequirements {
  description: string;
  provider: 'azure' | 'aws' | 'gcp' | 'fabric';
  budget?: number;
  environment?: 'dev' | 'staging' | 'production';
  constraints?: string[];
  contextHistory?: { role: string; content: string }[];
}

export interface DiscussArchitectureResult {
  reply: string;
  conversationHistory: { role: 'user' | 'assistant'; content: string }[];
}

export const discussArchitecture = (payload: {
  message: string;
  conversationHistory: { role: 'user' | 'assistant'; content: string }[];
  provider: string;
  budget?: number;
  environment?: string;
}): Promise<DiscussArchitectureResult> =>
  api.post('/api/ai/architecture/discuss', payload, { timeout: 60000 }).then(r => r.data);

export interface DiagramData {
  nodes: any[];
  edges: any[];
}

export interface SuggestArchitectureResult {
  diagramData: DiagramData;
  assistantMessage?: string | null;
  metadata: {
    model: string;
    tokensUsed: number;
    costUsd: number;
    durationMs: number;
    nodeCount: number;
    edgeCount: number;
  };
}

export const suggestArchitecture = (
  requirements: ArchitectureRequirements
): Promise<SuggestArchitectureResult> =>
  api.post('/api/ai/architecture/suggest', requirements, { timeout: 120000 }).then(r => r.data);

export interface SuggestedVariable {
  name: string;
  displayName?: string;
  description?: string;
  defaultValue?: string;
  type: 'string' | 'number' | 'boolean' | 'select';
  scope: 'parameter' | 'variable';
}

export interface RefineArchitectureResult {
  diagramData: DiagramData;
  conversationHistory: { role: 'user' | 'assistant'; content: string }[];
  suggestedVariables: SuggestedVariable[];
  assistantMessage: string | null;
  metadata: {
    model: string;
    tokensUsed: number;
    costUsd: number;
    durationMs: number;
    nodeCount: number;
    edgeCount: number;
  };
}

export const refineArchitecture = (payload: {
  diagramData: DiagramData;
  instruction: string;
  provider: string;
  conversationHistory: { role: 'user' | 'assistant'; content: string }[];
}): Promise<RefineArchitectureResult> =>
  api.post('/api/ai/architecture/refine', payload, { timeout: 120000 }).then(r => r.data);

export interface ValidationIssue {
  severity: 'error' | 'warning' | 'info';
  message: string;
  rule: string;
}

export interface ValidationResult {
  score: number;
  issues: ValidationIssue[];
}

export interface CostBreakdownItem {
  resourceId: string;
  resourceType: string;
  name: string;
  monthly: number;
  hourly: number;
  currency: string;
}

export interface CostEstimate {
  totalMonthly: number;
  totalHourly: number;
  currency: string;
  breakdown: CostBreakdownItem[];
  unsupported: any[];
}

export interface ValidateArchitectureResult {
  validation: ValidationResult;
  cost: CostEstimate;
}

export const validateArchitecture = (payload: {
  nodes: any[];
  edges: any[];
  provider: string;
  environment?: string;
}): Promise<ValidateArchitectureResult> =>
  api.post('/api/ai/architecture/validate', payload, { timeout: 30000 }).then(r => r.data);

export interface TerraformFiles {
  main: string;
  variables: string;
  outputs: string;
}

export interface GenerateTerraformResult {
  terraform: TerraformFiles;
  metadata: {
    model: string;
    tokensUsed: number;
    costUsd: number;
    durationMs: number;
  };
}

export const generateTerraform = (payload: {
  nodes: any[];
  edges: any[];
  provider: string;
  environment?: string;
}): Promise<GenerateTerraformResult> =>
  api.post('/api/ai/architecture/terraform', payload, { timeout: 60000 }).then(r => r.data);

export const getProviderCatalog = (provider: string): Promise<{ provider: string; resources: any[] }> =>
  api.get(`/api/ai/architecture/catalog/${provider}`).then(r => r.data);

export interface DocumentationResult {
  techDoc: string;
  adrDoc: string;
  metadata: {
    model: string;
    tokensUsed: number;
    durationMs: number;
    generatedAt: string;
  };
}

export const generateDocumentation = (payload: {
  nodes: any[];
  edges: any[];
  provider: string;
  environment?: string;
  architectureName?: string;
  projectName?: string;
  costs?: any;
}): Promise<DocumentationResult> =>
  api.post('/api/ai/architecture/documentation', payload, { timeout: 180000 }).then(r => r.data);

export interface SavedDocumentation {
  tech_doc: string;
  adr_doc: string;
  metadata: DocumentationResult['metadata'];
  updated_at: string;
}

export const getSavedDocumentation = (architectureId: string): Promise<SavedDocumentation> =>
  api.get(`/api/ai/architecture/documentation/${architectureId}`).then(r => r.data);

export const saveDocumentation = (architectureId: string, payload: {
  techDoc: string;
  adrDoc: string;
  metadata: DocumentationResult['metadata'];
}): Promise<{ success: boolean }> =>
  api.put(`/api/ai/architecture/documentation/${architectureId}`, payload).then(r => r.data);

export interface DocumentationHistoryEntry {
  techDoc: string;
  adrDoc: string;
  metadata: DocumentationResult['metadata'];
  savedAt: string;
}

export const getDocumentationHistory = (architectureId: string): Promise<{ history: DocumentationHistoryEntry[] }> =>
  api.get(`/api/ai/architecture/documentation/${architectureId}/history`).then(r => r.data);

export interface ReviewFinding {
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  recommendation: string;
}

export interface ReviewCategory {
  score: number;
  findings: ReviewFinding[];
}

export interface ReviewRecommendation {
  priority: number;
  title: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
  description: string;
}

export interface ArchitectureReview {
  overallScore: number;
  summary: string;
  categories: {
    security: ReviewCategory;
    resilience: ReviewCategory;
    performance: ReviewCategory;
    cost: ReviewCategory;
    bestPractices: ReviewCategory;
  };
  topRecommendations: ReviewRecommendation[];
  strengths: string[];
  quickWins: string[];
}

export interface ReviewResult {
  review: ArchitectureReview;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const reviewArchitecture = (payload: {
  nodes: any[];
  edges: any[];
  provider: string;
  environment?: string;
  architectureName?: string;
}): Promise<ReviewResult> =>
  api.post('/api/ai/architecture/review', payload, { timeout: 90000 }).then(r => r.data);

// ─── FinOps ──────────────────────────────────────────────────────────────────

export interface FinopsRecommendation {
  category: string;
  title: string;
  description: string;
  action: string;
  estimatedSavings: number;
  effort: 'low' | 'medium' | 'high';
  risk: 'low' | 'medium' | 'high';
  priority: number;
}

export interface FinopsReport {
  summary: string;
  currentMonthlyCost: number | null;
  optimizedMonthlyCost: number;
  potentialSavings: number;
  savingsPercent: number;
  recommendations: FinopsRecommendation[];
  quickWins: string[];
  longTermActions: string[];
  benchmarks: { label: string; assessment: string; comment: string };
}

export interface FinopsResult {
  report: FinopsReport;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const getFinopsRecommendations = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string; costs?: any;
}): Promise<FinopsResult> =>
  api.post('/api/ai/architecture/finops', payload, { timeout: 60000 }).then(r => r.data);

// ─── Compare Variants ─────────────────────────────────────────────────────────

export interface ArchitectureVariant {
  id: string;
  name: string;
  angle: string;
  tagline: string;
  description: string;
  resources: { type: string; label: string; role: string }[];
  pros: string[];
  cons: string[];
  estimatedMonthlyCost: string;
  complexity: 'low' | 'medium' | 'high';
  bestFor: string;
  migrationEffort: 'low' | 'medium' | 'high';
  keyDifferences: string[];
}

export interface CompareVariantsResult {
  variants: ArchitectureVariant[];
  comparison: { dimensions: string[]; matrix: Record<string, Record<string, number>> } | null;
  recommendation: string;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const compareArchitectureVariants = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string; architectureName?: string; variantsCount?: number;
}): Promise<CompareVariantsResult> =>
  api.post('/api/ai/architecture/variants', payload, { timeout: 120000 }).then(r => r.data);

// ─── Runbook ──────────────────────────────────────────────────────────────────

export interface RunbookResult {
  runbook: string;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const generateRunbook = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string; architectureName?: string;
}): Promise<RunbookResult> =>
  api.post('/api/ai/architecture/runbook', payload, { timeout: 90000 }).then(r => r.data);

// ─── ADR ─────────────────────────────────────────────────────────────────────

export interface AdrResult {
  adrDoc: string;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const generateAdr = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string; architectureName?: string; reviewFindings?: any[];
}): Promise<AdrResult> =>
  api.post('/api/ai/architecture/adr', payload, { timeout: 120000 }).then(r => r.data);

// ─── Chat Infrastructure ──────────────────────────────────────────────────────

export interface ChatMessage { role: 'user' | 'assistant'; content: string; }

export interface ChatResult {
  reply: string;
  conversationHistory: ChatMessage[];
  metadata: { model: string; tokensUsed: number; durationMs: number };
}

export const chatInfra = (payload: {
  message: string;
  conversationHistory: ChatMessage[];
  nodes: any[]; edges: any[];
  provider: string; environment?: string;
  architectureName?: string; projectName?: string;
}): Promise<ChatResult> =>
  api.post('/api/ai/chat', payload, { timeout: 30000 }).then(r => r.data);

// ─── AI Content Cache ────────────────────────────────────────────────────────

export type AiCacheContentType = 'runbook' | 'adr' | 'review' | 'finops' | 'variants' | 'compliance' | 'migration' | 'optimization';

export interface AiCacheEntry {
  content: any;
  metadata: any;
  updatedAt: string;
}

export const getAiCache = (architectureId: string, contentType: AiCacheContentType): Promise<AiCacheEntry> =>
  api.get(`/api/ai/cache/${architectureId}/${contentType}`).then(r => r.data);

export const saveAiCache = (architectureId: string, contentType: AiCacheContentType, content: any, metadata?: any): Promise<{ success: boolean }> =>
  api.post(`/api/ai/cache/${architectureId}/${contentType}`, { content, metadata }).then(r => r.data);

export const deleteAiCache = (architectureId: string, contentType: AiCacheContentType): Promise<{ success: boolean }> =>
  api.delete(`/api/ai/cache/${architectureId}/${contentType}`).then(r => r.data);

export interface AiCacheHistoryEntry {
  content: any;
  metadata: any;
  savedAt: string;
}

export const getAiCacheHistory = (architectureId: string, contentType: AiCacheContentType): Promise<{ history: AiCacheHistoryEntry[] }> =>
  api.get(`/api/ai/cache/${architectureId}/${contentType}/history`).then(r => r.data);

// ─── Compliance Scanner ───────────────────────────────────────────────────────

export type ComplianceFramework = 'cis' | 'iso27001' | 'rgpd' | 'hipaa' | 'pci_dss';

export interface ComplianceFinding {
  severity: 'critical' | 'high' | 'medium' | 'low';
  control: string;
  title: string;
  description: string;
  recommendation: string;
  resource: string;
}

export interface ComplianceFrameworkResult {
  score: number;
  status: 'compliant' | 'partial' | 'non_compliant' | 'not_applicable';
  findings: ComplianceFinding[];
}

export interface ComplianceReport {
  overallScore: number;
  summary: string;
  frameworks: {
    cis: ComplianceFrameworkResult;
    iso27001: ComplianceFrameworkResult;
    rgpd: ComplianceFrameworkResult;
    hipaa: ComplianceFrameworkResult;
    pci_dss: ComplianceFrameworkResult;
  };
  criticalActions: string[];
  complianceByResource: { resourceType: string; resourceLabel: string; issues: number; worstSeverity: string }[];
  certificationReadiness: Record<string, { ready: boolean; blockers: number }>;
}

export interface ComplianceScanResult {
  report: ComplianceReport;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const scanCompliance = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string;
  architectureName?: string; frameworks?: ComplianceFramework[];
}): Promise<ComplianceScanResult> =>
  api.post('/api/ai/architecture/compliance', payload, { timeout: 120000 }).then(r => r.data);

// ─── Migration Assistant ──────────────────────────────────────────────────────

export interface MigrationResourceFound {
  type: string;
  name: string;
  count: number;
  notes?: string;
}

export interface MigrationPhase {
  phase: number;
  title: string;
  duration: string;
  tasks: string[];
  deliverables: string[];
  risks: string[];
}

export interface MigrationGeniacMapping {
  existing: string;
  geniacType: string;
  equivalentService: string;
  migrationNotes: string;
}

export interface MigrationReport {
  analysis: {
    summary: string;
    sourceProvider: string;
    resourcesFound: MigrationResourceFound[];
    complexity: 'low' | 'medium' | 'high';
    estimatedEffort: string;
    mainChallenges: string[];
  };
  migrationPlan: {
    phases: MigrationPhase[];
    prerequisites: string[];
    rollbackStrategy: string;
    testingApproach: string;
  };
  suggestedNodes: any[];
  suggestedEdges: any[];
  geniacMapping: MigrationGeniacMapping[];
  estimatedMonthlyCost: string;
  costComparison: string;
}

export interface MigrationResult {
  report: MigrationReport;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const analyzeMigration = (payload: {
  existingInfra: string;
  provider: string;
  environment?: string;
  targetArchitectureName?: string;
  currentNodes?: any[];
}): Promise<MigrationResult> =>
  api.post('/api/ai/architecture/migration', payload, { timeout: 120000 }).then(r => r.data);

// ─── Presales Assistant ───────────────────────────────────────────────────────

export interface PresalesResult {
  proposal: string;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const generatePresalesProposal = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string;
  architectureName?: string; projectName?: string; clientName?: string;
  context?: string; costs?: any; language?: 'fr' | 'en';
}): Promise<PresalesResult> =>
  api.post('/api/ai/architecture/presales', payload, { timeout: 90000 }).then(r => r.data);

// ─── Incremental Update ───────────────────────────────────────────────────────

export interface IncrementalDocUpdate {
  changeLog: string;
  updatedSections: { section: string; before: string; after: string; reason: string }[];
}

export interface IncrementalAdrUpdate {
  newDecision: string | null;
  content: string | null;
  updatedDecisions: string[];
}

export interface IncrementalTerraformUpdate {
  resourcesAdded: { type: string; name: string; snippet: string }[];
  resourcesRemoved: string[];
  resourcesModified: { name: string; changes: string[] }[];
  variablesUpdated: string[];
}

export interface IncrementalUpdateReport {
  summary: string;
  impact: 'low' | 'medium' | 'high';
  affectedAreas: string[];
  docUpdate: IncrementalDocUpdate;
  adrUpdate: IncrementalAdrUpdate;
  terraformUpdate: IncrementalTerraformUpdate;
  recommendations: string[];
  rollbackSteps: string[];
}

export interface IncrementalUpdateResult {
  update: IncrementalUpdateReport;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const generateIncrementalUpdate = (payload: {
  currentNodes: any[]; currentEdges: any[];
  previousNodes?: any[]; previousEdges?: any[];
  provider: string; environment?: string;
  architectureName?: string; changeDescription?: string;
}): Promise<IncrementalUpdateResult> =>
  api.post('/api/ai/architecture/incremental', payload, { timeout: 90000 }).then(r => r.data);

// ─── Drift Detection ──────────────────────────────────────────────────────────

export interface DriftItem {
  severity: 'critical' | 'high' | 'medium' | 'low';
  recommendation: string;
}

export interface MissingInCloud extends DriftItem {
  canvasType: string;
  canvasLabel: string;
}

export interface UndocumentedInCloud extends DriftItem {
  cloudName: string;
  cloudType: string;
}

export interface ConfigDriftItem {
  resourceName: string;
  field: string;
  canvasValue: string;
  cloudValue: string;
  severity: 'high' | 'medium' | 'low';
}

export interface DriftAction {
  priority: number;
  title: string;
  type: 'deploy' | 'document' | 'remove' | 'update';
  description: string;
}

export interface DriftReport {
  driftScore: number;
  summary: string;
  status: 'in_sync' | 'minor_drift' | 'major_drift' | 'critical_drift';
  missingInCloud: MissingInCloud[];
  undocumentedInCloud: UndocumentedInCloud[];
  configDrift: ConfigDriftItem[];
  actions: DriftAction[];
  summary_counts: { missing: number; undocumented: number; config_drift: number };
}

export interface DriftDetectionResult {
  driftReport: DriftReport;
  cloudResourcesCount: number;
  fetchErrors: string[];
  localDiff: { matched: number; missing: number; undocumented: number };
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string; connectionName: string; resourceGroupName?: string };
}

export const detectArchitectureDrift = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string;
  architectureName?: string; connectionId: string;
}): Promise<DriftDetectionResult> =>
  api.post('/api/ai/architecture/drift', payload, { timeout: 120000 }).then(r => r.data);

// ─── Continuous Optimization ─────────────────────────────────────────────────

export interface OptimizationItem {
  id: string;
  category: 'cost' | 'performance' | 'security' | 'reliability' | 'sustainability';
  title: string;
  description: string;
  currentState: string;
  targetState: string;
  effort: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  estimatedSavingPercent?: number;
  implementationSteps: string[];
  priority: number;
}

export interface OptimizationReport {
  overallScore: number;
  summary: string;
  optimizations: OptimizationItem[];
  quickWins: string[];
  estimatedTotalSaving: { percent: number; description: string };
  topPriorities: string[];
}

export interface OptimizationResult {
  report: OptimizationReport;
  resourcesAnalyzed: number;
  metadata: { model: string; tokensUsed: number; durationMs: number; generatedAt: string };
}

export const optimizeArchitecture = (payload: {
  nodes: any[]; edges: any[]; provider: string; environment?: string;
  architectureName?: string; costs?: any;
}): Promise<OptimizationResult> =>
  api.post('/api/ai/architecture/optimize', payload, { timeout: 90000 }).then(r => r.data);
